import type { MediaImageInterface } from '@hypercodestudio/basler-components';

import type {
  LightingAdvisorQuestion,
  LightingAnswers
} from '~/types/lightingAdvisor';
import { mapQuestionInputsToSelectOptions } from '~/utils/lightingAdvisor/mapQuestionInputsToSelectOptions';
import { getFilteredQuestionInputs } from '~/utils/lightingAdvisor/questionDependencies';

/**
 * Where the question fields are rendered. `wizard-two-column` and
 * `modal-two-column` place questions side by side, the others stack them.
 */
export type LightingAdvisorQuestionLayout =
  'plp-list' | 'wizard-grid' | 'wizard-two-column' | 'modal-two-column';

/** Questions rendered as radio buttons instead of choice cards. */
const RADIO_QUESTION_IDS = [
  'a2s2q3',
  'a2s2q4',
  'a2s3q1',
  'a2s3q2',
  'a2s3q3',
  'a2s4q0'
];
const CHECKBOX_QUESTION_IDS = ['a2s4q1'];
/** Radio questions with labels short enough to sit in one row. */
const INLINE_OPTION_QUESTION_IDS = ['a2s4q0'];
/** Option lists too long for a single column; they get a three-column grid. */
const WIDE_OPTION_LIST_QUESTION_IDS = ['a2s3q3'];
/** Choice cards that fit four across in the wizard/modal grid. */
const FOUR_COLUMN_GRID_QUESTION_IDS = ['a2s5q1'];
/** Choice cards rendered as a list even in the wizard/modal. */
const LIST_DISPLAY_QUESTION_IDS = ['a2s5q2'];

/** Working distance, rendered as a single numeric input. */
export const DISTANCE_QUESTION_ID = 'a2s2q1';
/** Object size, split across a length and a width input. */
export const DIMENSION_QUESTION_ID = 'a2s2q2';
/** Peak wavelength, nested inside the light color question in the modal. */
export const NESTED_QUESTION_ID = 'a2s3q3';

export function isRadioQuestion(questionId: string): boolean {
  return RADIO_QUESTION_IDS.includes(questionId);
}

export function isInlineOptionQuestion(questionId: string): boolean {
  return INLINE_OPTION_QUESTION_IDS.includes(questionId);
}

export function isCheckboxQuestion(questionId: string): boolean {
  return CHECKBOX_QUESTION_IDS.includes(questionId);
}

export function isWideOptionListQuestion(questionId: string): boolean {
  return WIDE_OPTION_LIST_QUESTION_IDS.includes(questionId);
}

/** Column count for `SelectableOptionItems` in wizard/modal grid layouts. */
export function choiceGridColumns(questionId: string): 3 | 4 {
  return FOUR_COLUMN_GRID_QUESTION_IDS.includes(questionId) ? 4 : 3;
}

export function isListDisplayQuestion(questionId: string): boolean {
  return LIST_DISPLAY_QUESTION_IDS.includes(questionId);
}

export function isTwoColumnLayout(
  layout: LightingAdvisorQuestionLayout
): boolean {
  return layout === 'wizard-two-column' || layout === 'modal-two-column';
}

export function buildQuestionChoiceOptions(
  question: LightingAdvisorQuestion,
  answers: LightingAnswers,
  optionImages?: Readonly<Partial<Record<string, MediaImageInterface>>>
) {
  return mapQuestionInputsToSelectOptions(
    getFilteredQuestionInputs(question, answers),
    optionImages
  );
}
